import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Building2, Users, Briefcase, Map } from 'lucide-react';
import { Card, ICON_PX } from '../ui';
import { cn } from '../ui/lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { fetchOrgHierarchy } from '../../utils/api';

const LEVEL_CONFIG = {
  system:     { color: 'purple', Icon: Building2, bg: 'bg-purple-50', border: 'border-purple-500', text: 'text-purple-600', hover: 'hover:bg-purple-100' },
  region:     { color: 'blue',   Icon: Map,       bg: 'bg-blue-50',   border: 'border-blue-500',   text: 'text-blue-600',   hover: 'hover:bg-blue-100' },
  practice:   { color: 'green',  Icon: Briefcase, bg: 'bg-green-50',  border: 'border-green-500',  text: 'text-green-600',  hover: 'hover:bg-green-100' },
  department: { color: 'amber',  Icon: Users,     bg: 'bg-amber-50',  border: 'border-amber-500',  text: 'text-amber-600',  hover: 'hover:bg-amber-100' },
  team:       { color: 'slate',  Icon: Users,     bg: 'bg-slate-50',  border: 'border-slate-400',  text: 'text-slate-600',  hover: 'hover:bg-slate-100' },
};

function OrgTreeNode({ node, depth = 0 }) {
  const [isOpen, setIsOpen] = useState(depth < 2);
  const config = LEVEL_CONFIG[node.level] || LEVEL_CONFIG.team;
  const hasChildren = node.children && node.children.length > 0;
  const childCount = node.children?.length || 0;

  return (
    <div className={cn(depth > 0 && 'ml-6 mt-2', depth > 0 && `border-l-4 ${config.border} pl-4`)}>
      <div
        className={cn(
          'flex items-center gap-3 p-3 rounded-lg cursor-pointer transition',
          config.bg, config.hover
        )}
        onClick={() => hasChildren && setIsOpen(!isOpen)}
      >
        {hasChildren ? (
          isOpen
            ? <ChevronDown size={ICON_PX.md} className={config.text} />
            : <ChevronRight size={ICON_PX.md} className={config.text} />
        ) : (
          <div className="w-4" />
        )}
        <config.Icon size={ICON_PX.lg} className={config.text} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-body truncate">{node.name}</div>
          <div className="text-caption text-text-muted truncate capitalize">{node.level}</div>
        </div>
        {hasChildren && (
          <span className="text-caption text-text-muted shrink-0">
            <span className="font-medium">{childCount}</span> {childCount === 1 ? 'child' : 'children'}
          </span>
        )}
      </div>
      {isOpen && hasChildren && (
        <div>
          {node.children.map(child => (
            <OrgTreeNode key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function TeamHierarchy() {
  const { getToken } = useAuth();
  const [hierarchy, setHierarchy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetchOrgHierarchy(getToken)
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data) && data.length > 0) {
          setHierarchy(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.message || 'Failed to load hierarchy');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [getToken]);

  if (loading) {
    return (
      <Card className="p-6">
        <div className="text-body text-text-muted">Loading organizational structure...</div>
      </Card>
    );
  }

  if (error || !hierarchy) {
    return (
      <Card className="p-6">
        <div className="text-body text-text-muted">
          {error || 'No organizational hierarchy configured. Set up your org structure in Settings.'}
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="p-6">
        <h3 className="text-body font-bold mb-1">Organizational Structure</h3>
        <p className="text-caption text-text-muted mb-4">View and navigate your reporting structure</p>
        <div className="space-y-2">
          {hierarchy.map(node => (
            <OrgTreeNode key={node.id} node={node} depth={0} />
          ))}
        </div>
      </Card>
    </div>
  );
}

export default TeamHierarchy;
